import React, { useState, useEffect } from 'react';
import { Save, User, Percent, RefreshCw, AlertCircle, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import * as api from '../../services/api';

const CommissionsTab = ({ users, currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [pipelines, setPipelines] = useState([]);
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState({
    user_id: '',
    user_name: '',
    default_rate: 20,
    pipeline_rates: [],
    service_rates: []
  });
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadUserConfig(selectedUserId);
    }
  }, [selectedUserId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [pipelinesRes, servicesRes] = await Promise.all([
        api.getPipelines(),
        api.getEvaluationServices()
      ]);
      
      const pipelinesData = pipelinesRes.data || pipelinesRes || [];
      setPipelines(Array.isArray(pipelinesData) ? pipelinesData : []);
      
      const servicesData = servicesRes.data || servicesRes || [];
      setServices(Array.isArray(servicesData) ? servicesData : []);
      
      // Auto-select first user if available
      if (users && users.length > 0) {
        setSelectedUserId(users[0].id);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserConfig = async (userId) => {
    try {
      const res = await api.getUserCommissions(userId);
      const data = res.data || res;
      
      // Find user name
      const user = users.find(u => u.id === userId);
      
      // Initialize pipeline rates with all pipelines
      const pipelineRates = pipelines.map(p => {
        const existing = (data.pipeline_rates || []).find(pr => pr.pipeline_id === p.id);
        return {
          pipeline_id: p.id,
          pipeline_name: p.name,
          rate: existing ? existing.rate : (data.default_rate || 20)
        };
      });
      
      // Initialize service rates with all services
      const serviceRates = services.map(s => {
        const existing = (data.service_rates || []).find(sr => sr.service_name === s);
        return {
          service_name: s,
          rate: existing ? existing.rate : (data.default_rate || 20)
        };
      });
      
      setConfig({
        user_id: userId,
        user_name: user?.name || data.user_name || '',
        default_rate: data.default_rate || 20,
        pipeline_rates: pipelineRates,
        service_rates: serviceRates
      });
    } catch (error) {
      console.error('Error loading user config:', error);
      // Set default config
      const user = users.find(u => u.id === userId);
      setConfig({
        user_id: userId,
        user_name: user?.name || '',
        default_rate: 20,
        pipeline_rates: pipelines.map(p => ({
          pipeline_id: p.id,
          pipeline_name: p.name,
          rate: 20
        })),
        service_rates: services.map(s => ({
          service_name: s,
          rate: 20
        }))
      });
    }
  };

  const handleDefaultRateChange = (value) => {
    const rate = parseFloat(value) || 0;
    setConfig(prev => ({
      ...prev,
      default_rate: rate
    }));
  };

  const handlePipelineRateChange = (pipelineId, value) => {
    const rate = parseFloat(value) || 0;
    setConfig(prev => ({
      ...prev,
      pipeline_rates: prev.pipeline_rates.map(pr =>
        pr.pipeline_id === pipelineId ? { ...pr, rate } : pr
      )
    }));
  };

  const handleServiceRateChange = (serviceName, value) => {
    const rate = parseFloat(value) || 0;
    setConfig(prev => ({
      ...prev,
      service_rates: prev.service_rates.map(sr =>
        sr.service_name === serviceName ? { ...sr, rate } : sr
      )
    }));
  };

  const applyDefaultToAll = () => {
    setConfig(prev => ({
      ...prev,
      pipeline_rates: prev.pipeline_rates.map(pr => ({ ...pr, rate: prev.default_rate })),
      service_rates: prev.service_rates.map(sr => ({ ...sr, rate: prev.default_rate }))
    }));
  };

  const saveConfig = async () => {
    if (!selectedUserId) return;
    
    try {
      setSaving(true);
      setSaveMessage(null);
      
      await api.updateUserCommissions(selectedUserId, config);
      
      setSaveMessage({ type: 'success', text: 'Configuración guardada correctamente' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setSaveMessage({ type: 'error', text: 'Error al guardar la configuración' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commissions-tab-content">
      {/* Header */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Percent className="text-[#D4A843]" size={20} />
            Comisiones por Usuario
          </CardTitle>
          <p className="text-sm text-gray-500">
            Configure las tasas de comisión para cada usuario según el embudo de ventas o servicio de cotización.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Seleccionar Usuario
              </label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="user-commission-select">
                  <User size={16} className="mr-2" />
                  <SelectValue placeholder="Seleccione un usuario" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-32">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Tasa Base (%)
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={config.default_rate}
                onChange={(e) => handleDefaultRateChange(e.target.value)}
                className="text-center"
                data-testid="default-rate-input"
              />
            </div>
            
            <Button 
              variant="outline" 
              onClick={applyDefaultToAll}
              className="whitespace-nowrap"
            >
              <RefreshCw size={16} className="mr-2" />
              Aplicar a Todos
            </Button>
            
            <Button 
              onClick={saveConfig}
              disabled={!selectedUserId || saving}
              className="bg-[#D4A843] hover:bg-[#c49a3a] whitespace-nowrap"
              data-testid="save-commissions-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Guardar
            </Button>
          </div>
          
          {saveMessage && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              saveMessage.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {saveMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
              {saveMessage.text}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedUserId && (
        <>
          {/* Pipeline Rates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Comisiones por Embudo</CardTitle>
              <p className="text-sm text-gray-500">
                Asigne porcentajes específicos para cada embudo de ventas. Los nuevos embudos aparecerán automáticamente.
              </p>
            </CardHeader>
            <CardContent>
              {config.pipeline_rates.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No hay embudos configurados. Cree embudos en la sección de Embudos de Venta.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {config.pipeline_rates.map(pr => (
                    <div 
                      key={pr.pipeline_id} 
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm">{pr.pipeline_name}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={pr.rate}
                          onChange={(e) => handlePipelineRateChange(pr.pipeline_id, e.target.value)}
                          className="w-20 text-center"
                          data-testid={`pipeline-rate-${pr.pipeline_id}`}
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Service Rates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Comisiones por Servicio de Cotización</CardTitle>
              <p className="text-sm text-gray-500">
                Asigne porcentajes específicos para cada tipo de servicio. Los nuevos servicios de cotizaciones aparecerán automáticamente.
              </p>
            </CardHeader>
            <CardContent>
              {config.service_rates.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No hay servicios de cotización configurados. Los servicios se crean al generar cotizaciones.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {config.service_rates.map(sr => (
                    <div 
                      key={sr.service_name} 
                      className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm">{sr.service_name}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={sr.rate}
                          onChange={(e) => handleServiceRateChange(sr.service_name, e.target.value)}
                          className="w-20 text-center"
                          data-testid={`service-rate-${sr.service_name}`}
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Preview */}
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader>
              <CardTitle className="text-lg text-amber-800">Resumen de Configuración</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-amber-700 font-medium">Usuario</p>
                  <p className="text-lg font-bold text-gray-800">{config.user_name || 'No seleccionado'}</p>
                </div>
                <div>
                  <p className="text-sm text-amber-700 font-medium">Tasa Base</p>
                  <p className="text-lg font-bold text-gray-800">{config.default_rate}%</p>
                </div>
                <div>
                  <p className="text-sm text-amber-700 font-medium">Tasas Personalizadas</p>
                  <p className="text-lg font-bold text-gray-800">
                    {config.pipeline_rates.filter(pr => pr.rate !== config.default_rate).length} embudos, {' '}
                    {config.service_rates.filter(sr => sr.rate !== config.default_rate).length} servicios
                  </p>
                </div>
              </div>
              
              {/* Show non-default rates */}
              <div className="mt-4 pt-4 border-t border-amber-200">
                <p className="text-sm text-amber-700 font-medium mb-2">Tasas diferentes a la base:</p>
                <div className="flex flex-wrap gap-2">
                  {config.pipeline_rates
                    .filter(pr => pr.rate !== config.default_rate)
                    .map(pr => (
                      <span key={pr.pipeline_id} className="px-2 py-1 bg-white rounded text-sm border border-amber-200">
                        {pr.pipeline_name}: <strong>{pr.rate}%</strong>
                      </span>
                    ))
                  }
                  {config.service_rates
                    .filter(sr => sr.rate !== config.default_rate)
                    .map(sr => (
                      <span key={sr.service_name} className="px-2 py-1 bg-white rounded text-sm border border-blue-200">
                        {sr.service_name}: <strong>{sr.rate}%</strong>
                      </span>
                    ))
                  }
                  {config.pipeline_rates.filter(pr => pr.rate !== config.default_rate).length === 0 &&
                   config.service_rates.filter(sr => sr.rate !== config.default_rate).length === 0 && (
                    <span className="text-sm text-gray-500">Todas las tasas usan el valor base</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CommissionsTab;
